import React, { useEffect, useRef, useState } from 'react';
import { format, isSameDay } from 'date-fns';
import { messagingAPI } from '@/services/api';

const hasSafeAttachment = (message) => {
  if (!message?.file_storage_key || !message?.content) return false;
  try {
    const parsed = new URL(message.content, window.location.origin);
    return ['http:', 'https:'].includes(parsed.protocol)
      && parsed.pathname.includes('/api/messaging/files/');
  } catch {
    return false;
  }
};

const AuthenticatedAttachment = ({ message }) => {
  const [objectUrl, setObjectUrl] = useState(null);
  const objectUrlRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(message.type === 'image');

  const generationRef = useRef(0);
  const pendingRef = useRef(null);
  const load = () => {
    if (objectUrlRef.current) return Promise.resolve(objectUrlRef.current);
    if (pendingRef.current) return pendingRef.current;
    const generation = generationRef.current;
    setLoading(true); setFailed(false);
    const request = (async () => {
      try {
        const blob = await messagingAPI.downloadAttachment(message);
        if (generationRef.current !== generation) return null;
        const nextUrl = URL.createObjectURL(blob);
        objectUrlRef.current = nextUrl; setObjectUrl(nextUrl);
        return nextUrl;
      } catch {
        if (generationRef.current === generation) setFailed(true);
        return null;
      } finally {
        if (generationRef.current === generation) { setLoading(false); pendingRef.current = null; }
      }
    })();
    pendingRef.current = request;
    return request;
  };

  useEffect(() => {
    generationRef.current += 1;
    objectUrlRef.current = null; pendingRef.current = null;
    setObjectUrl(null); setFailed(false);
    if (message.type === 'image') load();
    return () => {
      generationRef.current += 1;
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message.message_id, message.file_storage_key]);

  const download = async () => {
    const url = await load();
    if (!url) return;
    const link = document.createElement('a');
    link.href = url;
    link.download = message.file_name || 'attachment';
    link.rel = 'noopener noreferrer';
    link.click();
  };

  if (failed) return <div className="text-sm"><p>Attachment unavailable. It may have expired or the connection was interrupted.</p><button type="button" onClick={download} className="underline">Retry attachment</button></div>;
  if (message.type === 'image') {
    if (loading || !objectUrl) return <p className="text-sm">Loading image…</p>;
    return (
      <button type="button" onClick={download} className="block" aria-label={`Download ${message.file_name || 'image'}`}>
        <img
          src={objectUrl}
          alt={message.file_name || 'Image attachment'}
          className="max-w-full rounded-md max-h-48 object-contain"
        />
      </button>
    );
  }
  return (
    <div className="flex items-center space-x-2">
      <button type="button" onClick={download} disabled={loading} className="underline disabled:opacity-60">
        {loading ? 'Preparing download…' : (message.file_name || 'Download file')}
      </button>
    </div>
  );
};

const MessageThread = ({ messages, currentUserId, otherPartyName, onRetry, search = '' }) => {
  const threadRef = useRef(null);

  useEffect(() => {
    // Scroll only the transcript. scrollIntoView also moves the app's outer
    // scroll containers and can hide the conversation header or bottom nav.
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages]);

  if (!messages || messages.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <div className="text-center">
          <p className="text-lg mb-2">No messages yet</p>
          <p className="text-sm">Start the conversation by sending a message</p>
        </div>
      </div>
    );
  }

  const visibleMessages = search.trim() ? messages.filter((message) => `${message.content || ''} ${message.file_name || ''} ${message.caption || ''}`.toLowerCase().includes(search.trim().toLowerCase())) : messages;
  return (
    <div ref={threadRef} className="message-thread" role="log" aria-label="Messages" aria-live="polite">
      {search && visibleMessages.length === 0 && <p className="chat-date">No messages match your search.</p>}
      {visibleMessages.map((message, index) => {
        const isOwnMessage = message.sender_id === currentUserId;
        const messageDate = new Date(message.created_at);
        const attachmentSafe = message.type === 'text' || hasSafeAttachment(message);
        if (message.type === 'call') return <div key={message.message_id} className="chat-date" aria-label="Call history">{message.content} · {format(messageDate, 'MMM d, h:mm a')}</div>;
        return (
          <React.Fragment key={message.message_id}>
          {(index === 0 || !isSameDay(messageDate, new Date(visibleMessages[index - 1].created_at))) && <div className="chat-date">{format(messageDate, 'EEEE, MMM d')}</div>}
          <div className={`message-row ${isOwnMessage ? 'is-own' : ''}`}>
            <div className="message-bubble" aria-label={isOwnMessage ? 'Your message' : `Message from ${otherPartyName}`}>
              <div>
                {message.type === 'text' && <p className="whitespace-pre-wrap break-words">{message.content}</p>}
                {message.type !== 'text' && attachmentSafe && <AuthenticatedAttachment message={message} />}
                {message.type !== 'text' && !attachmentSafe && <p className="text-sm">Attachment unavailable.</p>}
              {message.caption && <p className="mt-2 whitespace-pre-wrap break-words">{message.caption}</p>}
              </div>
              <span className="message-time">
                {format(messageDate, 'h:mm a')}{message.delivery_state === 'sending' ? ' · Sending…' : ''}
              </span>
              {message.delivery_state === 'failed' && (
                <button type="button" className="mt-1 text-xs font-semibold text-red-600 underline" onClick={() => onRetry?.(message)}>
                  Send failed · Retry
                </button>
              )}
            </div>
          </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default MessageThread;
